import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import {
  ManagerProgressionTaskSlug,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { ClaimedTask } from '#src/components/missions/ClaimedTask/index.tsx';
import type { RewardUnlockedStory_reward } from '#src/components/missions/stories/RewardUnlockedStory/__generated__/index.graphql.ts';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';
import { useTaskButtonProps } from '#src/components/missions/useTaskButtonProps.tsx';

import type {
  ClaimDailyActionSubTaskMutation,
  ClaimDailyActionSubTaskMutationVariables,
  DailyActionSubTask_taskInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  display: flex;
  padding: var(--intermediate-unit);
  border: 1px solid var(--c-nd-150);
  &.clickable {
    cursor: pointer;
  }
  border-radius: var(--unit);
  position: relative;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.5) 30.87%,
    rgba(0, 0, 0, 0.29) 68.73%,
    rgba(0, 0, 0, 0) 96.68%
  );

  &.isClaimable {
    cursor: default;
    border-color: var(--c-blue-400);
    box-shadow: inset 0 0 var(--unit) var(--c-blue-400);
  }
`;

const StyledNeonEdge = styled(NeonEdge)`
  width: 70%;
  height: 50%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  transform: rotateZ(180deg);
`;
const StyledClaimedTask = styled(ClaimedTask)`
  border-radius: var(--unit);
`;

const Title = styled(LabelL)`
  text-align: left;
`;

const CLAIM_DAILY_ACTION_SUB_TASK_MUTATION = gql`
  mutation ClaimDailyActionSubTaskMutation($taskId: ID!, $sport: Sport!) {
    claimTask(input: { taskId: $taskId }) {
      rewards {
        id
        ...RewardUnlockedStory_reward
      }
      currentUser {
        slug
        dailyRunTask(sport: $sport) {
          id
          ... on DailyActionTask {
            id
            liveScore
            thresholds {
              count
              current
            }
          }
        }
        ...useClaimTask_currentUser
      }
      task {
        id
        aasmState
        parentTask {
          id
          ... on DailyActionTask {
            id
            aasmState
            liveScore
          }
        }
      }
    }
  }
  ${
    // oxlint-disable-next-line sorare/no-detached-fragments
    RewardUnlockedStory.fragments.anyRewardInterface
  }
  ${
    // oxlint-disable-next-line sorare/no-detached-fragments
    useClaimTask.fragments.currentUser
  }
` as TypedDocumentNode<
  ClaimDailyActionSubTaskMutation,
  ClaimDailyActionSubTaskMutationVariables
>;

const ClaimButton = ({
  task,
  onClaimed,
}: {
  task: DailyActionSubTask_taskInterface;
  onClaimed: (rewards: RewardUnlockedStory_reward[]) => void;
}) => {
  const { sport } = useSportContext();
  const [claimDailyActionSubTask] = useMutation(
    CLAIM_DAILY_ACTION_SUB_TASK_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );

  return (
    <Button
      size="compact"
      color="primary"
      onClick={async () => {
        const { data } = await claimDailyActionSubTask({
          variables: { taskId: task.id, sport },
        });
        onClaimed(data?.claimTask?.rewards || []);
      }}
    >
      <FormattedMessage {...glossary.claim} />
    </Button>
  );
};

type Props = {
  task: DailyActionSubTask_taskInterface;
  onDropChallengeClick: Nullable<() => void>;
  onDecisivePlayerPickerClick: Nullable<() => void>;
};

export const DailyActionSubTask = ({
  task,
  onDropChallengeClick,
  onDecisivePlayerPickerClick,
}: Props) => {
  const { to } = useTaskButtonProps({
    task,
  });
  const [rewards, setRewards] = useState<RewardUnlockedStory_reward[]>([]);

  // Only packs are worth interrupting the run: they need the reveal flow.
  // Any other reward (essence, XP…) is claimed silently.
  const onClaimed = (claimedRewards: RewardUnlockedStory_reward[]) => {
    if (!claimedRewards.some(isTypeFn('CardPackReward'))) {
      return;
    }
    setRewards(claimedRewards);
  };

  const { title, target, aasmState, name } = task;
  const isClaimable = aasmState === ManagerProgressionTaskState.COMPLETED;

  // Rendered here rather than inside ClaimButton so it survives the row
  // switching to its CLAIMED state (which unmounts the Claim button) right
  // after the claim resolves.
  const rewardsDialog = rewards.length > 0 && (
    <StepperDialog
      onClose={() => setRewards([])}
      steps={[
        {
          bodyElement: ({ onNext }) => (
            <Fragment key="reward-unlocked-story">
              <RewardUnlockedStory onClose={onNext} rewards={rewards} />
            </Fragment>
          ),
        },
      ]}
    />
  );

  if (aasmState === ManagerProgressionTaskState.CLAIMED) {
    return (
      <>
        <StyledClaimedTask claimedTask={task} />
        {rewardsDialog}
      </>
    );
  }

  const getExtraProps = () => {
    if (isClaimable) {
      return null;
    }
    if (
      name === ManagerProgressionTaskSlug.COMPLETE_TASK &&
      onDropChallengeClick
    ) {
      return {
        as: ButtonBase,
        onClick: onDropChallengeClick,
      };
    }
    if (
      name === ManagerProgressionTaskSlug.UPSERT_TASK_APPEARANCES &&
      onDecisivePlayerPickerClick
    ) {
      return {
        as: ButtonBase,
        onClick: onDecisivePlayerPickerClick,
      };
    }
    if (aasmState === ManagerProgressionTaskState.READY && !!to) {
      return {
        as: Link,
        to,
      };
    }
    return null;
  };
  const extraProps = getExtraProps();
  const progress = aasmState === ManagerProgressionTaskState.READY ? 0 : 1;
  return (
    <>
      <Root
        className={classNames({ isClaimable, clickable: !!to })}
        {...(extraProps || {})}
      >
        {!!isClaimable && <StyledNeonEdge color="var(--c-blue-400)" />}
        <SBHorizontal>
          <Title bold>{title}</Title>
          {isClaimable ? (
            <ClaimButton task={task} onClaimed={onClaimed} />
          ) : (
            !!extraProps && <ChevronRightBold />
          )}
        </SBHorizontal>
        <SBHorizontal>
          <Progress
            value={progress}
            max={target}
            color="linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)"
          />
          <Horizontal gap={0}>
            <LabelS bold>{progress}</LabelS>
            <LabelS>&nbsp;/&nbsp;{target}</LabelS>
          </Horizontal>
        </SBHorizontal>
      </Root>
      {rewardsDialog}
    </>
  );
};

DailyActionSubTask.fragments = {
  taskInterface: gql`
    fragment DailyActionSubTask_taskInterface on TaskInterface {
      id
      name
      title
      aasmState
      target
      ...useTaskButtonProps_taskInterface
      ...ClaimedTask_taskInterface
    }
    ${useTaskButtonProps.fragments.taskInterface}
    ${ClaimedTask.fragments.taskInterface}
  ` as TypedDocumentNode<DailyActionSubTask_taskInterface>,
};
